import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { AuthProvider, RequireAuth, useAuth } from "./lib/auth";
import { TenantProvider } from "./lib/tenant";
import { PerspectiveProvider } from "./lib/perspective";
import { ToastProvider } from "./lib/toast";
import { Shell } from "./components/Shell";
import { CommandPalette } from "./components/CommandPalette";
import { AdvisorDrawer } from "./components/AdvisorDrawer";

import LandingPage from "./pages/Landing";
import OnboardingConversationPage from "./pages/OnboardingConversation";
import CompanyProfilePage from "./pages/CompanyProfile";
import CockpitPage from "./pages/Cockpit";
import CommandCenterPage from "./pages/CommandCenter";
import MetricsPage from "./pages/Metrics";
import SourcesPage from "./pages/Sources";
import SourceQualityPage from "./pages/SourceQuality";
import DriverTreesPage from "./pages/DriverTrees";
import DriverTreeCanvasPage from "./pages/DriverTreeCanvas";
import BriefingsPage from "./pages/Briefings";
import BriefingDetailPage from "./pages/BriefingDetail";
import AgentRunsPage from "./pages/AgentRuns";
import AgentsLivePage from "./pages/AgentsLive";
import CompliancePage from "./pages/Compliance";
import OnboardingPage from "./pages/Onboarding";
import KpiCandidatesPage from "./pages/KpiCandidates";
import AdvisorPage from "./pages/Advisor";
import PotentialAnalysisPage from "./pages/PotentialAnalysis";
import RoiMonteCarloPage from "./pages/RoiMonteCarlo";
import ProcessLandscapePage from "./pages/ProcessLandscape";
import BenchmarksPage from "./pages/Benchmarks";
import UseCasesPage from "./pages/UseCases";
import RoadmapPage from "./pages/Roadmap";
import AchievementsPage from "./pages/Achievements";
import TransformationOSPage from "./pages/TransformationOS";
import HelpPage from "./pages/Help";
import BriefingAgentPage from "./pages/BriefingAgent";
import SteuerkreisPage from "./pages/Steuerkreis";
import InterventionenPage from "./pages/Interventionen";
import StartPage from "./pages/Start";
import BenachrichtigungenPage from "./pages/Benachrichtigungen";
import DecisionsPage from "./pages/Decisions";
import MeetingsPage from "./pages/Meetings";
import KundencockpitPage from "./pages/Kundencockpit";
import AiReadyCheckPage from "./pages/AiReadyCheck";
import KanbanPage from "./pages/Kanban";
import FormularePage from "./pages/Formulare";
import ControlSystemPage from "./pages/ControlSystem";
import "./styles.css";
import "./styles.additions.css";
import "./styles.tokens.css";
import "./styles.system.css";
import "./styles.proactive.css";
import "./styles.w3c.css";
import "./styles.help.css";

const qc = new QueryClient({ defaultOptions: { queries: { retry: 1, staleTime: 30_000 } } });

function AppShell() {
  return (
    <>
      <Shell />
      <CommandPalette />
      <AdvisorDrawer />
    </>
  );
}

function LegacyTreeRedirect() {
  const path = window.location.pathname.replace(/^\/trees\//, "/app/trees/");
  return <Navigate to={path} replace />;
}
function LegacyBriefingRedirect() {
  const path = window.location.pathname.replace(/^\/briefings\//, "/app/briefings/");
  return <Navigate to={path} replace />;
}
function LegacySourcesQuality() {
  const path = window.location.pathname.replace(/^\/sources\//, "/app/sources/");
  return <Navigate to={path} replace />;
}

function DemoRedirect() {
  const { startDemo } = useAuth();
  const started = React.useRef(false);
  React.useEffect(() => {
    if (started.current) return;
    started.current = true;
    startDemo();
  }, [startDemo]);
  return <div className="bootstrapping">Sandbox-Demo wird gestartet...</div>;
}

function LoginRedirect({ action }: { action: "login" | "register" }) {
  const { login, register } = useAuth();
  const started = React.useRef(false);
  React.useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (action === "login") login();
    else register();
  }, [action, login, register]);
  return <div className="bootstrapping">{action === "login" ? "Sign in wird geöffnet..." : "Registrierung wird geöffnet..."}</div>;
}

const root = createRoot(document.getElementById("root")!);
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={qc}>
        <AuthProvider>
          <TenantProvider>
            <PerspectiveProvider>
              <ToastProvider>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/demo" element={<DemoRedirect />} />
                  <Route path="/login" element={<LoginRedirect action="login" />} />
                  <Route path="/register" element={<LoginRedirect action="register" />} />

                  <Route path="/onboard" element={<Navigate to="/onboard/start" replace />} />
                  <Route path="/onboard/start" element={<RequireAuth><OnboardingConversationPage /></RequireAuth>} />

                  <Route path="/app" element={<RequireAuth><AppShell /></RequireAuth>}>
                    <Route index element={<Navigate to="/app/start" replace />} />
                    <Route path="start" element={<StartPage />} />
                    <Route path="steuerungssystem" element={<ControlSystemPage />} />
                    <Route path="steuerkreis" element={<SteuerkreisPage />} />
                    <Route path="hilfe" element={<HelpPage />} />
                    <Route path="briefing-agent" element={<BriefingAgentPage />} />
                    <Route path="cockpit" element={<CommandCenterPage />} />
                    <Route path="command-center" element={<CommandCenterPage />} />
                    <Route path="classic" element={<CockpitPage />} />
                    <Route path="company" element={<CompanyProfilePage />} />
                    <Route path="advisor" element={<AdvisorPage />} />
                    <Route path="potential" element={<PotentialAnalysisPage />} />
                    <Route path="process-landscape" element={<ProcessLandscapePage />} />
                    <Route path="interventionen" element={<InterventionenPage />} />
                    <Route path="transformation" element={<TransformationOSPage />} />
                    <Route path="roi" element={<RoiMonteCarloPage />} />
                    <Route path="benchmarks" element={<BenchmarksPage />} />
                    <Route path="use-cases" element={<UseCasesPage />} />
                    <Route path="roadmap" element={<RoadmapPage />} />
                    <Route path="achievements" element={<AchievementsPage />} />
                    <Route path="metrics" element={<MetricsPage />} />
                    <Route path="sources" element={<SourcesPage />} />
                    <Route path="sources/:sourceId/quality" element={<SourceQualityPage />} />
                    <Route path="trees" element={<DriverTreesPage />} />
                    <Route path="trees/:kpiId" element={<DriverTreeCanvasPage />} />
                    <Route path="briefings" element={<BriefingsPage />} />
                    <Route path="briefings/:briefingId" element={<BriefingDetailPage />} />
                    <Route path="agents" element={<AgentRunsPage />} />
                    <Route path="agents-live" element={<AgentsLivePage />} />
                    <Route path="compliance" element={<CompliancePage />} />
                    <Route path="onboarding" element={<OnboardingConversationPage />} />
                    <Route path="admin/tenant-onboarding" element={<OnboardingPage />} />
                    <Route path="candidates" element={<KpiCandidatesPage />} />
                    <Route path="benachrichtigungen" element={<BenachrichtigungenPage />} />
                    <Route path="decisions" element={<DecisionsPage />} />
                    <Route path="meetings" element={<MeetingsPage />} />
                    <Route path="kundencockpit" element={<KundencockpitPage />} />
                    <Route path="ai-ready-check" element={<AiReadyCheckPage />} />
                    <Route path="kanban" element={<KanbanPage />} />
                    <Route path="formulare" element={<FormularePage />} />
                  </Route>

                  <Route path="/cockpit" element={<Navigate to="/app/cockpit" replace />} />
                  <Route path="/company" element={<Navigate to="/app/company" replace />} />
                  <Route path="/advisor" element={<Navigate to="/app/advisor" replace />} />
                  <Route path="/process-landscape" element={<Navigate to="/app/process-landscape" replace />} />
                  <Route path="/interventionen" element={<Navigate to="/app/interventionen" replace />} />
                  <Route path="/benchmarks" element={<Navigate to="/app/benchmarks" replace />} />
                  <Route path="/use-cases" element={<Navigate to="/app/use-cases" replace />} />
                  <Route path="/roadmap" element={<Navigate to="/app/roadmap" replace />} />
                  <Route path="/achievements" element={<Navigate to="/app/achievements" replace />} />
                  <Route path="/sources" element={<Navigate to="/app/sources" replace />} />
                  <Route path="/sources/:sourceId/quality" element={<LegacySourcesQuality />} />
                  <Route path="/trees" element={<Navigate to="/app/trees" replace />} />
                  <Route path="/trees/:kpiId" element={<LegacyTreeRedirect />} />
                  <Route path="/briefings" element={<Navigate to="/app/briefings" replace />} />
                  <Route path="/briefings/:briefingId" element={<LegacyBriefingRedirect />} />
                  <Route path="/command-center" element={<Navigate to="/app/command-center" replace />} />
                  <Route path="/start" element={<Navigate to="/app/start" replace />} />
                  <Route path="/steuerungssystem" element={<Navigate to="/app/steuerungssystem" replace />} />
                  <Route path="/decisions" element={<Navigate to="/app/decisions" replace />} />
                  <Route path="/meetings" element={<Navigate to="/app/meetings" replace />} />
                  <Route path="/kundencockpit" element={<Navigate to="/app/kundencockpit" replace />} />
                  <Route path="/ai-ready-check" element={<Navigate to="/app/ai-ready-check" replace />} />
                  <Route path="/kanban" element={<Navigate to="/app/kanban" replace />} />
                  <Route path="/formulare" element={<Navigate to="/app/formulare" replace />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </ToastProvider>
            </PerspectiveProvider>
          </TenantProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);
